/**
 * Hala CRM - Roles & Permissions Page
 * View roles and their permissions (read-only)
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Check, X, Info, Users, Eye, Edit2, Trash2, UserPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('hala_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const COLORS = {
  primary: '#1B3A6B',
  gold: '#C5A572',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
};

const ROLE_ICONS = {
  admin: { color: '#DC2626', bg: '#FEE2E2' },
  supervisor: { color: '#2563EB', bg: '#DBEAFE' },
  consultant: { color: '#059669', bg: '#D1FAE5' },
  group_user: { color: '#D97706', bg: '#FEF3C7' },
};

const RESOURCE_LABELS = {
  users: 'المستخدمون',
  groups: 'المجموعات',
  programs: 'البرامج',
  requests: 'الطلبات',
  reports: 'التقارير',
  settings: 'الإعدادات',
};

const ACTION_LABELS = {
  create: 'إنشاء',
  read: 'عرض',
  update: 'تعديل',
  delete: 'حذف',
  manage: 'إدارة',
  assign: 'إسناد',
  reassign: 'إعادة إسناد',
  claim: 'المطالبة',
  add_note: 'إضافة ملاحظة',
  export: 'تصدير',
  view_all: 'عرض الكل',
  view_group: 'عرض المجموعة',
  view_assigned: 'عرض المُسند',
  view_pool: 'عرض المجمع',
  view_own: 'عرض الخاص',
};

const SCOPE_LABELS = {
  global: { label: 'عالمي', description: 'وصول كامل لجميع البيانات', color: COLORS.error },
  group: { label: 'المجموعة', description: 'وصول للمجموعات المُسندة فقط', color: COLORS.primary },
  assigned: { label: 'المُسند', description: 'وصول للطلبات المُسندة فقط', color: COLORS.success },
  pool: { label: 'المجمع', description: 'وصول لمجمع طلبات المجموعة', color: COLORS.warning },
};

function PermissionBadge({ hasPermission }) {
  return hasPermission ? (
    <div style={{
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: COLORS.successLight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <Check size={14} color={COLORS.success} />
    </div>
  ) : (
    <div style={{
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: COLORS.bgDark,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <X size={14} color={COLORS.textMuted} />
    </div>
  );
}

function RoleCard({ role, isSelected, onClick }) {
  const roleStyle = ROLE_ICONS[role.role_id] || { color: COLORS.textMuted, bg: COLORS.bgDark };
  const scopeStyle = SCOPE_LABELS[role.scope] || SCOPE_LABELS.assigned;

  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: isSelected ? `${COLORS.primary}10` : COLORS.white,
        borderRadius: '12px',
        border: `2px solid ${isSelected ? COLORS.primary : COLORS.border}`,
        padding: '20px',
        cursor: 'pointer',
        transition: 'all 0.2s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          backgroundColor: roleStyle.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Shield size={24} color={roleStyle.color} />
        </div>
        <div>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
            {role.role_name_ar}
          </h3>
          <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.textMuted }}>
            {role.role_name_en}
          </p>
        </div>
      </div>

      <p style={{ margin: '0 0 12px', fontSize: '13px', color: COLORS.textMuted, lineHeight: 1.5 }}>
        {role.description_ar}
      </p>

      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 12px',
        borderRadius: '20px',
        backgroundColor: `${scopeStyle.color}15`,
      }}>
        <Eye size={14} color={scopeStyle.color} />
        <span style={{ fontSize: '12px', fontWeight: 500, color: scopeStyle.color }}>
          نطاق الوصول: {scopeStyle.label}
        </span>
      </div>
    </div>
  );
}

function PermissionsMatrix({ role }) {
  if (!role) return null;

  const permissions = role.permissions || {};
  const allResources = Object.keys(RESOURCE_LABELS);
  const allActions = ['create', 'read', 'update', 'delete', 'manage', 'assign', 'reassign', 'claim', 'add_note', 'view_all', 'view_group', 'view_assigned', 'view_pool', 'view_own', 'export'];

  // Get unique actions used by this role
  const usedActions = new Set();
  Object.values(permissions).forEach(actions => {
    actions.forEach(action => usedActions.add(action));
  });
  const relevantActions = allActions.filter(a => usedActions.has(a));

  return (
    <div style={{
      backgroundColor: COLORS.white,
      borderRadius: '12px',
      border: `1px solid ${COLORS.border}`,
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '16px 20px',
        borderBottom: `1px solid ${COLORS.border}`,
        backgroundColor: COLORS.bg,
      }}>
        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: COLORS.text }}>
          مصفوفة الصلاحيات - {role.role_name_ar}
        </h3>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{
                padding: '12px 16px',
                textAlign: 'right',
                fontSize: '12px',
                fontWeight: 600,
                color: COLORS.textMuted,
                backgroundColor: COLORS.bg,
                borderBottom: `1px solid ${COLORS.border}`,
                position: 'sticky',
                right: 0,
              }}>
                المورد
              </th>
              {relevantActions.map(action => (
                <th key={action} style={{
                  padding: '12px 8px',
                  textAlign: 'center',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: COLORS.textMuted,
                  backgroundColor: COLORS.bg,
                  borderBottom: `1px solid ${COLORS.border}`,
                  whiteSpace: 'nowrap',
                }}>
                  {ACTION_LABELS[action] || action}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allResources.map(resource => {
              const resourcePermissions = permissions[resource] || [];
              return (
                <tr key={resource}>
                  <td style={{
                    padding: '12px 16px',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: COLORS.text,
                    borderBottom: `1px solid ${COLORS.border}`,
                    backgroundColor: COLORS.white,
                    position: 'sticky',
                    right: 0,
                  }}>
                    {RESOURCE_LABELS[resource]}
                  </td>
                  {relevantActions.map(action => (
                    <td key={action} style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      borderBottom: `1px solid ${COLORS.border}`,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <PermissionBadge hasPermission={resourcePermissions.includes(action)} />
                      </div>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function CRMRolesPage() {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rolesRes, statsRes] = await Promise.all([
          axios.get(`${API}/crm/roles`, { headers: getAuthHeaders() }),
          axios.get(`${API}/crm/stats`, { headers: getAuthHeaders() }),
        ]);
        
        setRoles(rolesRes.data || []);
        setStats(statsRes.data);
        
        // Auto-select first role
        if (rolesRes.data?.length > 0) {
          setSelectedRole(rolesRes.data[0]);
        }
      } catch (e) {
        console.error('Failed to fetch data:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>
        جاري التحميل...
      </div>
    );
  }

  return (
    <div style={{ padding: '0' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: 700, color: COLORS.text }}>
          الأدوار والصلاحيات
        </h1>
        <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
          عرض أدوار المستخدمين وصلاحياتهم في النظام
        </p>
      </div>

      {/* Info Banner */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '16px 20px',
        backgroundColor: `${COLORS.primary}10`,
        borderRadius: '12px',
        marginBottom: '24px',
        border: `1px solid ${COLORS.primary}30`,
      }}>
        <Info size={20} color={COLORS.primary} style={{ flexShrink: 0, marginTop: '2px' }} />
        <div>
          <p style={{ margin: 0, fontSize: '14px', color: COLORS.text, fontWeight: 500 }}>
            الأدوار محددة مسبقاً في النظام
          </p>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
            لا يمكن تعديل الأدوار الأساسية. يمكنك إسناد المستخدمين للأدوار المناسبة من صفحة المستخدمين.
          </p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          {Object.entries(stats.users?.by_role || {}).map(([roleId, count]) => {
            const role = roles.find(r => r.role_id === roleId);
            const style = ROLE_ICONS[roleId] || { color: COLORS.textMuted, bg: COLORS.bgDark };
            return (
              <div key={roleId} style={{ backgroundColor: COLORS.white, padding: '16px 20px', borderRadius: '12px', border: `1px solid ${COLORS.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: style.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Users size={18} color={style.color} />
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '11px', color: COLORS.textMuted }}>{role?.role_name_ar || roleId}</p>
                    <p style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: style.color }}>{count}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '24px' }}>
        {/* Roles List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {roles.map(role => (
            <RoleCard
              key={role.role_id}
              role={role}
              isSelected={selectedRole?.role_id === role.role_id}
              onClick={() => setSelectedRole(role)}
            />
          ))}
        </div>

        {/* Permissions Matrix */}
        <div>
          <PermissionsMatrix role={selectedRole} />

          {/* Scope Description */}
          {selectedRole && (
            <div style={{
              marginTop: '16px',
              padding: '16px 20px',
              backgroundColor: COLORS.bg,
              borderRadius: '12px',
              border: `1px solid ${COLORS.border}`,
            }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, color: COLORS.text }}>
                شرح نطاق الوصول
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                {Object.entries(SCOPE_LABELS).map(([key, val]) => (
                  <div 
                    key={key}
                    style={{
                      padding: '12px',
                      backgroundColor: selectedRole.scope === key ? `${val.color}15` : COLORS.white,
                      borderRadius: '8px',
                      border: `1px solid ${selectedRole.scope === key ? val.color : COLORS.border}`,
                    }}
                  >
                    <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: val.color }}>
                      {val.label}
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: COLORS.textMuted }}>
                      {val.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
